"use client";
import React, { Fragment, useEffect, useRef, useState } from "react";
import Image from "next/image";
import PropTypes from "prop-types";
import cn from "classnames";
import Link from "next/link";

import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import ButtonLink from "@/next-src/components/ui/button-link/ButtonLink";
import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";
import Calendly from "@/next-src/components/ui/calendly/Calendly";
import { useBrochureUpdate } from "@/next-src/context/Brochure";

import comCSS from "./Header.module.scss";

const Header = ({ data = {}, isHeroBlockShown }) => {
  const { width: windowWidth } = useWindowDimensions();
  const [menuItemId, setMenuItemId] = useState("");
  const [isMobileMenuOpened, setIsMobileMenuOpened] = useState(false);
  const [isBgDark, setIsBgDark] = useState(null);
  const navbarRef = useRef();
  const { setIsShown } = useBrochureUpdate();

  // this is to show percent data animation. The code sets it only once, when a user scrolled to the section
  useEffect(() => {
    //after-hero-page-wrap-unique
    if (typeof window !== "undefined") {
      const onScroll = () => {
        const panel = document.querySelector(".after-hero-page-wrap-unique");

        if (!panel || !navbarRef.current) return null;
        const isInView =
          panel.getBoundingClientRect().top <=
          navbarRef.current.getBoundingClientRect().bottom;
        if (isInView) {
          setIsBgDark(true);
        } else {
          setIsBgDark(false);
        }
      };

      window.addEventListener("scroll", onScroll, false);
      window.addEventListener("resize", onScroll, false);

      // Call handler right away so state gets updated with initial window size
      onScroll();

      return () => {
        window.removeEventListener("scroll", onScroll, false);
        window.removeEventListener("resize", onScroll, false);
      };
    }
  }, []);

  const {
    navItems,
    headerDesktopButton,
    headerMobileButton,
    headerMobileAskButton,
    logo,
    calendlyButtonSuccess,
    calendlyButtonSuccessMobile,
  } = data || {};

  const onMenuItemOver = (item) => {
    if (windowWidth < 992) return;
    setMenuItemId(item.id);
  };

  const onMenuItemLeave = () => {
    if (windowWidth < 992) return;
    setMenuItemId("");
  };

  const onMenuItemClick = (item) => {
    setMenuItemId((id) => (id === item.id ? "" : item.id));
  };

  const getText = (el, keyLG, keyXS) => {
    if (windowWidth < 992) {
      return convertHTMLtoJSX(el?.[keyXS]);
    }
    return convertHTMLtoJSX(el?.[keyLG]);
  };

  const navJSX = navItems
    .filter((item) => item.isActive)
    .map((item) => {
      if (item.subMenu) {
        return (
          <Fragment key={item.id}>
            <a
              href={item.href}
              className="navbar_dropdown hide w-inline-block"
              style={{ opacity: 1 }}
            >
              <div className="hide-tablet">{convertHTMLtoJSX(item.title)}</div>
              <div className="hide-desktop">
                {convertHTMLtoJSX(item.titleMobile)}
              </div>
              <Image
                height={20}
                width={20}
                src="/images/home/63da6f698bd69456c5ba9c9e_add.svg"
                loading="lazy"
                alt=""
                className="navbar_dropdown_icon"
              />
            </a>
            <div
              data-hover="true"
              data-delay="0"
              data-w-id="24c902dd-deba-697d-dc05-23221f610220"
              className={cn("navbar-dropdown w-dropdown")}
              style={{ zIndex: menuItemId === item.id ? 901 : "" }}
              onMouseEnter={() => onMenuItemOver(item)}
              onMouseLeave={onMenuItemLeave}
            >
              <div
                className={cn(
                  "navbar_link is-dropdown w-dropdown-toggle",
                  comCSS.navbarLink,
                  {
                    ["w--open"]: menuItemId === item.id,
                  }
                )}
                id="w-dropdown-toggle-0"
                aria-controls="w-dropdown-list-0"
                aria-haspopup="menu"
                aria-expanded={menuItemId === item.id}
                role="button"
                tabIndex="0"
                style={{ opacity: 1 }}
              >
                <a href={item.href}>
                  <div className="hide-tablet">
                    {convertHTMLtoJSX(item.title)}
                  </div>
                  <div className="hide-desktop">
                    {convertHTMLtoJSX(item.titleMobile)}
                  </div>
                </a>
                <Image
                  src="/images/home/63da6f698bd69456c5ba9c9e_add.svg"
                  loading="lazy"
                  alt=""
                  width={20}
                  height={20}
                  className={cn(
                    "navbar_dropdown_icon",
                    comCSS.navbarDropdownIcon,
                    {
                      [comCSS.active]: menuItemId === item.id,
                    }
                  )}
                  onClick={() => onMenuItemClick(item)}
                />
              </div>
              <nav
                className={cn(
                  "navbar_dropdown_wrapper_native w-dropdown-list",
                  {
                    ["w--open"]: menuItemId === item.id,
                  }
                )}
                id="w-dropdown-list-0"
                aria-labelledby="w-dropdown-toggle-0"
              >
                <div className="container-large">
                  <div className="navbar-dropdown_inner-grid">
                    <div className="navbar_dropdown_inner-wrap-left">
                      <div className="max-width-full">
                        <div className="margin-bottom margin-xsmall hide-tablet">
                          <div className="text-note">
                            {convertHTMLtoJSX(item.title)}
                          </div>
                        </div>
                        {item.subMenu?.items
                          ?.filter((subItem) => subItem.isActive)
                          ?.map((subItem, subIndex) => (
                            <a
                              key={subIndex}
                              href={subItem.href}
                              className={cn(
                                "navbar_dropdown_clinic-link w-inline-block w--current",
                                comCSS.navbarDropdownClinicLink
                              )}
                              tabIndex="0"
                            >
                              <div className="dropdown-item-left small-gap">
                                {getText(subItem, "title", "titleMobile")}
                                {getText(
                                  subItem,
                                  "country",
                                  "countryMobile"
                                ) ? (
                                  <div className="small-tag grey with-icon">
                                    <div>
                                      {getText(
                                        subItem,
                                        "country",
                                        "countryMobile"
                                      )}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                              <div
                                className={cn(
                                  "accordion-icon-rounded-wrap",
                                  comCSS.accordionIconRoundedWrap
                                )}
                              >
                                <div className="moving-arrows-mask">
                                  <Image
                                    width={24}
                                    height={24}
                                    src="/images/home/63f639c0ef2babd91cc4ce1e_arrow_forward_02.svg"
                                    loading="lazy"
                                    alt=""
                                    className="moving-inner-arrow"
                                  />
                                  <Image
                                    width={24}
                                    height={24}
                                    src="/images/home/63f639c0ef2babd91cc4ce1e_arrow_forward_02.svg"
                                    loading="lazy"
                                    alt=""
                                    className="moving-inner-arrow"
                                  />
                                  <Image
                                    width={24}
                                    height={24}
                                    src="/images/home/63f639c0ef2babd91cc4ce1e_arrow_forward_02.svg"
                                    loading="lazy"
                                    alt=""
                                    className="moving-inner-arrow"
                                  />
                                </div>
                              </div>
                            </a>
                          ))}
                      </div>
                    </div>
                    <div className="navbar_dropdown_inner-wrap-right">
                      <div className="navbar_dropdown-right-section-img">
                        <ResponsiveImage
                          src={item.subMenu?.info?.img?.src}
                          lazyLoading
                          sizes="100vw"
                          alt={item.subMenu?.info?.img?.alt}
                          className="cover-image"
                        />
                      </div>
                      <div className="display-vertical-flex-left-top">
                        <div className="margin-bottom margin-tiny">
                          {convertHTMLtoJSX(item.subMenu?.info?.title)}
                        </div>
                        <div className="margin-bottom margin-small text-color-slate-grey">
                          {convertHTMLtoJSX(item.subMenu?.info?.text)}
                        </div>
                        <ButtonLink
                          data={item.subMenu?.info?.button}
                          className="custom-small-btn"
                          onClick={(e) => {
                            if (
                              item.subMenu?.info?.button?.url === "#brochure"
                            ) {
                              e.preventDefault();
                              setIsShown(true);
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
            </div>
          </Fragment>
        );
      } else {
        return (
          <a
            key={item.id}
            href={item.href}
            className="navbar_link w-nav-link"
            style={{ opacity: 1 }}
          >
            <span className="hide-tablet">{convertHTMLtoJSX(item.title)}</span>
            <span className="hide-desktop">
              {convertHTMLtoJSX(item.titleMobile)}
            </span>
          </a>
        );
      }
    });

  const navigationAttrs = isMobileMenuOpened
    ? {
        "data-nav-menu-open": true,
      }
    : {};

  const navigation = (
    <nav
      role="navigation"
      id="w-node-_24c902dd-deba-697d-dc05-23221f610219-5bb4f39f"
      className={cn("navbar2_menu w-nav-menu", comCSS.navigation, {
        [comCSS.navigationOpened]: isMobileMenuOpened,
      })}
      {...navigationAttrs}
    >
      {navJSX}
      <div className="open-nav-btns">
        {headerMobileButton.calendlyLink ? (
          <Calendly
            classNameBtn="is-icon is-inverted"
            isPulsingIcon
            data={{
              calendlyButton: headerMobileButton,
              calendlyButtonMobile: headerMobileButton,
              calendlyButtonSuccess: calendlyButtonSuccessMobile,
              calendlyButtonSuccessMobile,
            }}
          />
        ) : (
          <ButtonLink
            data={headerMobileButton}
            className="is-icon is-inverted"
            isPulsingIcon
          />
        )}
        <ButtonLink data={headerMobileAskButton} className="is-secondary" />
      </div>
    </nav>
  );

  const rightPart = (
    <div
      id="w-node-_24c902dd-deba-697d-dc05-23221f610261-5bb4f39f"
      className="navbar2_button-wrapper"
    >
      {headerDesktopButton.calendlyLink ? (
        <Calendly
          classNameBtn="is-icon is-small nav-btn"
          classNameBtnSuccess="hide-tablet"
          isPulsingIcon
          data={{
            calendlyButton: headerDesktopButton,
            calendlyButtonMobile: headerDesktopButton,
            calendlyButtonSuccess,
            calendlyButtonSuccessMobile: calendlyButtonSuccess,
          }}
        />
      ) : (
        <ButtonLink
          data={headerDesktopButton}
          className="is-icon is-small nav-btn"
          isPulsingIcon
        />
      )}
      <div
        className={cn("navbar2_menu-button w-nav-button", {
          "w--open": isMobileMenuOpened,
        })}
        style={{ WebkitUserSelect: "text" }}
        aria-label="menu"
        role="button"
        tabIndex="0"
        aria-controls="w-nav-overlay-0"
        aria-haspopup="menu"
        aria-expanded={isMobileMenuOpened}
        onClick={() => {
          setIsMobileMenuOpened(!isMobileMenuOpened);
        }}
      >
        <div
          className={cn("menu-icon2", {
            [comCSS.burgerActive]: isMobileMenuOpened,
          })}
        >
          <div className="menu-icon2_line-top"></div>
          <div className="menu-icon2_line-middle">
            <div className="menu-icon_line-middle-inner"></div>
          </div>
          <div className="menu-icon2_line-bottom"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      data-animation="default"
      className={cn("navbar2_component w-nav", comCSS.wrapper)}
      data-easing2="ease"
      fs-scrolldisable-element="smart-nav"
      data-easing="ease"
      data-collapse="medium"
      data-w-id="efcf45a9-6e1e-e049-f6e9-0d535bb4f39f"
      role="banner"
      data-duration="0"
    >
      <div
        className={cn("navbar2_container", comCSS.navbar, {
          [comCSS.navbarBgDark]: isBgDark || !isHeroBlockShown,
        })}
        ref={navbarRef}
      >
        <div
          className={cn("mobile-nav-bg-fill", comCSS.mobileNavBgFill, {
            [comCSS.mobileNavBgFillActive]: isMobileMenuOpened,
          })}
        ></div>
        <Link
          href="/"
          id="w-node-_24c902dd-deba-697d-dc05-23221f610217-5bb4f39f"
          className="navbar2_logo-link w-nav-brand"
          aria-label="home"
        >
          <ResponsiveImage
            width={128}
            height={32}
            sizes="128px"
            src={logo?.src}
            lazyLoading
            alt={logo?.alt}
            className="navbar2_logo"
          />
        </Link>
        {navigation}
        {rightPart}
      </div>
      <div
        className={cn("mobile-menu-bg", {
          [comCSS.mobileMenuBgActive]: isMobileMenuOpened,
        })}
      />
    </div>
  );
};

Header.propTypes = {
  data: PropTypes.shape({
    logo: PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      width: PropTypes.number, // Encourages providing width for logo
      height: PropTypes.number, // Encourages providing height for logo
    }),
    navItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.node, // Allows for various title content (e.g., strings or JSX)
        titleMobile: PropTypes.node,
        href: PropTypes.string.isRequired,
        isActive: PropTypes.bool,
        subMenu: PropTypes.shape({
          items: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              title: PropTypes.string,
              titleMobile: PropTypes.string,
              href: PropTypes.string.isRequired,
              country: PropTypes.string,
              countryMobile: PropTypes.string,
              isActive: PropTypes.bool,
            })
          ),
          info: PropTypes.shape({
            title: PropTypes.node, // Allows for various title content (e.g., strings or JSX)
            text: PropTypes.node, // Allows for various text content (e.g., strings or JSX)
            button: PropTypes.shape({
              text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
              href: PropTypes.string,
              url: PropTypes.string, // Consider using a single `href` prop
            }),
            img: PropTypes.shape({
              src: PropTypes.string.isRequired,
              alt: PropTypes.string.isRequired,
              width: PropTypes.number, // Encourages providing width for image
              height: PropTypes.number, // Encourages providing height for image
            }),
          }),
        }),
      })
    ),
    headerDesktopButton: PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      calendlyLink: PropTypes.string,
    }),
    headerMobileButton: PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      calendlyLink: PropTypes.string,
    }),
    headerMobileAskButton: PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string,
      url: PropTypes.string.isRequired,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonSuccess: PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonSuccessMobile: PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
  }),
  isHeroBlockShown: PropTypes.bool,
};

export default Header;
